// <promo-form-wizard> — pagina el formulario de captación de promociones (el
// `.rnk-PromoForm` del Formulario::FormularioComponent) en pasos, SIN tocar sus
// campos, validaciones, verificación SMS ni el contrato submit -> gracias. Porta
// la interacción del prototipo de diseño (ENH-RNK-3564, variante B) al patrón de
// custom elements del proyecto: la variante B del A/B de la landing Hipotecas 100.
//
// Degradación segura: sin JS, o si el setup falla, el formulario se muestra
// entero de una página (el submit y toda la lógica de servidor siguen intactos).
//
// Contrato de DOM que espera:
//   <promo-form-wizard data-wizard-steps='{"steps":[{"title":"…","fields":["nombre",…]},…]}'>
//     <form class="rnk-PromoForm">
//       <div class="rnk-PromoForm_Inner">
//         <fieldset name="fieldset_nombre">…</fieldset> …
//       </div>
//     </form>
//   </promo-form-wizard>
// Los fieldsets no asignados a ningún paso (cesión de datos, submit) caen al último.

const ERROR_ICON = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="7" x2="12" y2="13"></line><line x1="12" y1="16.5" x2="12.01" y2="16.5"></line></svg>'

class PromoFormWizard extends HTMLElement {
  connectedCallback () {
    // Idempotente: Turbo puede reconectar el elemento; no re-montamos.
    if (this.hasAttribute('data-wizard-ready') || this.hasAttribute('data-wizard-failed')) return

    const form = this.querySelector('form.rnk-PromoForm, .rnk-PromoForm')
    const cfg = this._parseConfig()
    if (!form || !cfg) return // degradación: formulario completo

    const inner = form.querySelector('.rnk-PromoForm_Inner') || form
    const fieldsets = Array.from(inner.querySelectorAll('fieldset'))
    if (!fieldsets.length) return

    try {
      this._build(form, inner, fieldsets, cfg)
      this.setAttribute('data-wizard-ready', '1')
    } catch (e) {
      // Degradación segura: restaura el formulario plano y no reintenta.
      try {
        fieldsets.forEach((fs) => inner.appendChild(fs))
        inner.querySelectorAll('.rk-wizard_Head, .rk-wizard_Step, .rk-wizard_Nav').forEach((el) => el.remove())
      } catch (_) { /* noop */ }
      this.setAttribute('data-wizard-failed', '1')
      if (window.console && console.error) console.error('PromoFormWizard:', e)
    }
  }

  _parseConfig () {
    try {
      const raw = this.getAttribute('data-wizard-steps')
      const cfg = raw ? JSON.parse(raw) : null
      if (!cfg || !Array.isArray(cfg.steps) || cfg.steps.length < 2) return null
      return cfg
    } catch (_) {
      return null
    }
  }

  _build (form, inner, fieldsets, cfg) {
    const labels = cfg.labels || {}

    const fieldName = (fs) => (fs.getAttribute('name') || '').replace(/^fieldset_/, '')
    const byName = {}
    fieldsets.forEach((fs) => { byName[fieldName(fs)] = fs })

    // Un contenedor por paso; dentro se mueven sus fieldsets en el orden pedido.
    const assigned = []
    const stepEls = cfg.steps.map((step) => {
      const el = document.createElement('div')
      el.className = 'rk-wizard_Step';
      (step.fields || []).forEach((name) => {
        const fs = byName[name]
        if (fs) { el.appendChild(fs); assigned.push(fs) }
      })
      return el
    })
    const last = stepEls[stepEls.length - 1]
    // El submit se reserva para el final del último paso: debe quedar POR DEBAJO de
    // los consentimientos (cesión / finalidad RGPD), nunca por encima del check.
    const submitFs = fieldsets.find((fs) => !assigned.includes(fs) && fs.querySelector('[type="submit"]'))
    // Resto de fieldsets sin asignar (cesión de datos, etc.) -> último paso, orden DOM.
    fieldsets.forEach((fs) => {
      if (!assigned.includes(fs) && fs !== submitFs) last.appendChild(fs)
    })
    // Hijos que no son fieldset (p. ej. consentimiento de finalidad, que se pinta
    // fuera del loop de campos) -> último paso, junto a la cesión, ANTES del submit.
    Array.from(inner.children).forEach((child) => {
      if (child.tagName !== 'FIELDSET') last.appendChild(child)
    })
    // El submit, siempre lo último del wizard (por debajo del check de promociones).
    if (submitFs) last.appendChild(submitFs)

    // Cabecera: contador + título + barra de progreso.
    const head = document.createElement('div')
    head.className = 'rk-wizard_Head'
    head.innerHTML =
      '<div class="rk-wizard_Meta"><span class="rk-wizard_Count"></span>' +
      '<span class="rk-wizard_Title"></span></div>' +
      '<div class="rk-wizard_Bar"><div class="rk-wizard_BarFill"></div></div>'

    // Navegación: Atrás + Siguiente (el submit real solo se ve en el último paso).
    const nav = document.createElement('div')
    nav.className = 'rk-wizard_Nav'
    const back = document.createElement('button')
    back.type = 'button'
    back.className = 'rk-wizard_Back'
    back.textContent = labels.back || 'Atrás'
    const next = document.createElement('button')
    next.type = 'button'
    next.className = 'rk-wizard_Next'
    next.textContent = labels.next || 'Siguiente'
    nav.appendChild(back)
    nav.appendChild(next)

    inner.insertBefore(head, inner.firstChild)
    stepEls.forEach((el) => inner.appendChild(el))
    inner.appendChild(nav)

    let current = 0
    const total = stepEls.length

    const stepOf = (input) => {
      for (let i = 0; i < stepEls.length; i++) if (stepEls[i].contains(input)) return i
      return -1
    }

    // --- Validación con mensajes inline (prevención de errores) ---
    const messageFor = (input) => {
      const v = input.validity
      if (v.valueMissing) {
        return input.tagName === 'SELECT' ? 'Selecciona una opción para poder continuar.' : 'Este campo es obligatorio.'
      }
      const emailMsg = 'Introduce un email válido, por ejemplo nombre@correo.com.'
      if (v.typeMismatch && input.type === 'email') return emailMsg
      if (v.patternMismatch && input.type === 'email') return emailMsg
      if (v.patternMismatch && input.type === 'tel') return 'Teléfono no válido: solo números, sin el prefijo.'
      if (v.rangeUnderflow) return `El valor mínimo es ${input.min}.`
      if (v.rangeOverflow) return `El valor máximo es ${input.max}.`
      if (v.customError) return input.validationMessage
      if (v.badInput) return 'Introduce solo números.'
      return input.validationMessage || 'Revisa este campo.'
    }

    const errorCell = (input) => {
      // Checkboxes: el error va bajo la frase (en el Row), no dentro del label en flex.
      if (input.type === 'checkbox') {
        return input.closest('.rnk-PromoForm_Row') || input.closest('fieldset') || input.parentNode
      }
      return input.closest('.rnk-PromoForm_Cell') || input.parentNode
    }

    const setError = (input, msg) => {
      const cell = errorCell(input)
      let el = cell.querySelector('.rk-wizard_Error')
      if (!el) {
        el = document.createElement('p')
        el.className = 'rk-wizard_Error'
        el.id = `${input.id || `rk-f-${Math.random().toString(36).slice(2)}`}-error`
        cell.appendChild(el)
      }
      el.innerHTML = `${ERROR_ICON}<span></span>`
      el.querySelector('span').textContent = msg
      input.classList.add('rk-is-invalid')
      input.setAttribute('aria-invalid', 'true')
      input.setAttribute('aria-describedby', el.id)
      input.setAttribute('data-rk-touched', '1')
    }

    const clearError = (input) => {
      const el = errorCell(input).querySelector('.rk-wizard_Error')
      if (el && el.parentNode) el.remove()
      input.classList.remove('rk-is-invalid')
      input.removeAttribute('aria-invalid')
      input.removeAttribute('aria-describedby')
    }

    let validating = false // suprime el handler 'invalid' en validaciones programáticas

    // Check verde en teléfono y email cuando el formato es válido.
    const updateValidMark = (input) => {
      if (input.type !== 'tel' && input.type !== 'email') return
      validating = true
      const ok = !!input.value && input.checkValidity()
      validating = false
      input.classList.toggle('rk-is-valid', ok)
    }

    const validateInput = (input) => {
      this._syncNumericValidity(input)
      validating = true
      const ok = input.checkValidity()
      validating = false
      updateValidMark(input)
      if (ok) { clearError(input); return true }
      setError(input, messageFor(input))
      return false
    }

    const visibleInputsValid = () => {
      const inputs = stepEls[current].querySelectorAll('input, select, textarea')
      let firstInvalid = null
      inputs.forEach((input) => {
        if (!validateInput(input) && !firstInvalid) firstInvalid = input
      })
      if (firstInvalid) {
        try { firstInvalid.focus({ preventScroll: false }) } catch (_) { firstInvalid.focus() }
      }
      return !firstInvalid
    }

    const show = (i) => {
      current = Math.max(0, Math.min(total - 1, i))
      stepEls.forEach((el, idx) => el.classList.toggle('is-active', idx === current))
      const count = head.querySelector('.rk-wizard_Count')
      const title = head.querySelector('.rk-wizard_Title')
      const fill = head.querySelector('.rk-wizard_BarFill')
      count.textContent = (labels.step || 'Paso {n} de {total}')
        .replace('{n}', current + 1).replace('{total}', total)
      title.textContent = (cfg.steps[current] && cfg.steps[current].title) || ''
      fill.style.width = `${((current + 1) / total) * 100}%`
      back.hidden = current === 0
      next.style.display = current === total - 1 ? 'none' : ''
    }

    next.addEventListener('click', () => {
      if (visibleInputsValid()) show(current + 1)
    })
    back.addEventListener('click', () => show(current - 1))

    // Feedback inmediato: al corregir un campo marcado, el error desaparece; al
    // salir de un campo ya tocado, se revalida (prevención antes del envío).
    const onLiveEdit = (ev) => {
      const t = ev.target
      if (!t) return
      if (t.hasAttribute && t.hasAttribute('data-rk-touched')) validateInput(t)
      else if (t.type === 'tel' || t.type === 'email') updateValidMark(t)
    }
    form.addEventListener('input', onLiveEdit)
    form.addEventListener('change', onLiveEdit)
    form.addEventListener('blur', (ev) => {
      const t = ev.target
      if (t && t.willValidate && (t.value !== '' || t.hasAttribute('data-rk-touched'))) validateInput(t)
    }, true)

    // Enter en pasos intermedios avanza en lugar de enviar.
    form.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' && current < total - 1 && ev.target && ev.target.tagName !== 'TEXTAREA') {
        ev.preventDefault()
        if (visibleInputsValid()) show(current + 1)
      }
    })

    // Si el submit final encuentra un campo inválido, salta a su paso y muestra el
    // error inline (sin bubble nativo). Solo actúa en el submit real: nunca roba el
    // foco durante la validación en vivo.
    form.addEventListener('invalid', (ev) => {
      ev.preventDefault()
      if (validating) return
      const i = stepOf(ev.target)
      if (i !== -1 && i !== current) show(i)
      validateInput(ev.target)
      try { ev.target.focus() } catch (_) { /* noop */ }
    }, true)

    // Transformamos los inputs numéricos al FINAL: si algo anterior en _build
    // lanza, el catch de connectedCallback restaura un formulario plano intacto
    // (los numéricos siguen como type="number" con su validación nativa min/max).
    this._setupNumericInputs(form)

    show(0)
  }

  // Campos numéricos: separador de miles (es-ES) mientras se escribe. `type="number"`
  // no admite puntos, así que se pasan a `text` + inputmode numérico conservando
  // min/max como validación propia. Antes del submit real, los valores viajan sin
  // puntos (payload limpio para el backend, idéntico al formulario de una página).
  _setupNumericInputs (form) {
    const digitsOnly = (v) => (v || '').replace(/\D/g, '')
    const formatMiles = (v) => v.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

    // Guarda min/max en data-* (los usa la validación propia) y los quita del input:
    // en `type="text"` los atributos nativos no aplican y estorbarían.
    const stashLimit = (input, attr, dataAttr) => {
      if (!input.hasAttribute(attr)) return
      input.setAttribute(dataAttr, input.getAttribute(attr))
      input.removeAttribute(attr)
    }

    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.setAttribute('data-rk-numeric', '1')
      stashLimit(input, 'min', 'data-rk-min')
      stashLimit(input, 'max', 'data-rk-max')
      input.removeAttribute('step')
      input.type = 'text'
      input.setAttribute('inputmode', 'numeric')
      input.setAttribute('autocomplete', 'off')
      input.addEventListener('input', (ev) => {
        let d = digitsOnly(input.value)
        // Borrar el último carácter (el € o el espacio) borra el último dígito.
        if (ev && ev.inputType === 'deleteContentBackward' && input._rkPrev === d && d) d = d.slice(0, -1)
        input._rkPrev = d
        const f = d ? `${formatMiles(d)} €` : ''
        if (input.value !== f) {
          input.value = f
          const pos = d ? f.length - 2 : 0
          try { input.setSelectionRange(pos, pos) } catch (_) { /* noop */ }
        }
      })
    })

    form.addEventListener('submit', () => {
      form.querySelectorAll('[data-rk-numeric]').forEach((input) => { input.value = digitsOnly(input.value) })
    })
  }

  _syncNumericValidity (input) {
    if (!input.hasAttribute || !input.hasAttribute('data-rk-numeric')) return
    const d = (input.value || '').replace(/\D/g, '')
    const min = input.getAttribute('data-rk-min')
    const max = input.getAttribute('data-rk-max')
    const formatMiles = (v) => v.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    let msg = ''
    if (d) {
      if (min !== null && +d < +min) msg = `El valor mínimo es ${formatMiles(min)} €.`
      else if (max !== null && +d > +max) msg = `El valor máximo es ${formatMiles(max)} €.`
    }
    input.setCustomValidity(msg)
  }
}

export default PromoFormWizard
